import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import { Provider } from "../../../data/entities";
import BackButton from "../../../shared/components/BackButton";
import CancelModal from "../../../shared/components/CancelModal";
import PageLoader from "../../../shared/components/LoadingPage";
import { api } from "../../../shared/hooks/api";
import { PageContentWrapper } from "../../../shared/styled/PageContentStyle";
import { error, success } from "../../../utils/toast";
import { normalizeProviderContactList } from "./providerData";
import TabNavigation from "./TabBar";
import Title from "./Title";

const processProvider = (providerContact: Provider) => {
    let contact_list: any[] = [];
    let materials = [];
    let outsource_materials = [];
    if (providerContact) {
        contact_list = normalizeProviderContactList(
            providerContact.contact_list
        ).map((contact: any) => {
            if (contact.contact) {
                return {
                    ...contact,
                    contact: {
                        ...contact.contact,
                    },
                };
            }
            return contact;
        });
        materials = (Array.isArray(providerContact.materials)
            ? providerContact.materials
            : []).map((item: any) => {
            if (item.material) {
                return {
                    ...item.material,
                    providerPrice: item.price,
                };
            }
            return item;
        });
        outsource_materials = (Array.isArray(providerContact.outsource_materials)
            ? providerContact.outsource_materials
            : []).map((item: any) => {
            if (item.material) {
                return {
                    ...item.material,
                    providerPrice: item.price,
                    note: item.note ?? item.material.note,
                    outsource_standards: item.outsource_standards ?? [],
                };
            }
            return item;
        });
    }
    return {
        ...providerContact,
        contact_list: contact_list,
        materials: materials,
        outsource_materials: outsource_materials,
    };
};

const processUpdateProvider = (providerContact: Provider) => {
    const materials = (Array.isArray(providerContact.materials)
        ? providerContact.materials
        : []).map((material: any) => ({
        material_id: material.id,
        price: material.providerPrice,
    }));
    const outsource_materials = (providerContact.outsource_materials || []).map((material: any) => ({
        material_id: material.id,
        price: material.providerPrice,
        note: material.note || "",
    }));

    const processedData = {
        ...providerContact,
        contact_list: normalizeProviderContactList(providerContact.contact_list).map(
            ({ id, contact }: any) => {
                return {
                    id,
                    contact: {
                        ...contact,
                    },
                };
            }
        ),
        materials: materials,
        outsource_materials: outsource_materials,
    };

    return processedData;
};

const ProviderDetail = () => {
    const { providerId } = useParams();
    const [validated, setValidated] = useState(false);
    const navigate = useNavigate();
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [
        { data, isLoading, setLocalData, setLocalUrl: setGetUrl },
        fetchProvider,
    ] = api.get(`/materials/providers/${providerId}/`);
    const provider = useMemo(() => processProvider(data), [data]);
    const [{ isDeleting }, deleteProvider] = api.delete(
        `/materials/providers/${providerId}/`
    );
    const [{ isUpdating, setLocalUrl: setUpdateUrl }, updateProvider] =
        api.patch(`/materials/providers/${providerId}/`);

    useEffect(() => {
        setGetUrl(`/materials/providers/${providerId}/`);
        setUpdateUrl(`/materials/providers/${providerId}/`);
    }, [providerId, setGetUrl, setUpdateUrl]);

    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isModified) {
                event.preventDefault();
                event.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const handleDeleteProvider = async () => {
        if (isDeleting) return;
        try {
            await deleteProvider();
            success("THÀNH CÔNG", "Xóa nhà cung cấp thành công");
        } catch (err) {
            error("Xóa nhà cung cấp thất bại", err);
        }
    };

    const handleSubmitProvider = async () => {
        if (isUpdating) return;
        try {
            await updateProvider(processUpdateProvider(provider));
            await fetchProvider();
            success("THÀNH CÔNG", "Cập nhật nhà cung cấp thành công");
        } catch (err) {
            error("Cập nhật nhà cung cấp thất bại", err);
        }
    };

    const handleBackClick = () => {
        confirmNavigation("/providers/");
    };

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setShowUnsavedDialog(true);
            setNavigationPath(path);
            return false;
        }

        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            await handleSubmitProvider();
            navigate(navigationPath);
        } catch (err) {
            error("Lỗi cập nhật nhà cung cấp", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleDataChange = useCallback((updateProvider: any) => {
        markAsModified();
        setLocalData({
            ...data,
            ...updateProvider,
        });
    }, [markAsModified, setLocalData, data]);

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    return (
        <PageContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            {isLoading && <PageLoader />}
            {!isLoading && (
                <Title
                    title={provider.name}
                    setValidated={setValidated}
                    deleteProvider={handleDeleteProvider}
                    handleSubmit={handleSubmitProvider}
                    onDataChange={markAsModified}
                    hasUnsavedChanges={isModified}
                    onCancelClick={() => confirmNavigation("/providers/")}
                    isSaving={isUpdating}
                    isDeleting={isDeleting}
                />
            )}
            {!isLoading && (
                <TabNavigation
                    validated={validated}
                    providerData={provider}
                    mergeProvider={handleDataChange}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isUpdating}
            />
        </PageContentWrapper>
    );
};

export default ProviderDetail;
